import { useState } from 'react'
import { Alert, Box, Button, Chip, Grid, LinearProgress, Paper, Stack, Typography } from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { DataTable, DataTableColumn } from '@components/common/DataTable'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import type {
  PerfActiveQuery,
  PerfDatabase,
  PerfPgStatement,
  PerfSeqScanTable,
  PerfTableStat,
  PerfUnusedIndex,
} from '@api/types/performance'
import { StatTile } from './StatTile'
import { SqlBlock } from './SqlBlock'
import { formatCount, formatMs, formatPercent, latencySeverity } from './format'

interface DatabaseTabProps {
  data?: PerfDatabase
  loading: boolean
  /** The catalogue request failed; shown instead of zeros that would read as an idle server. */
  error?: boolean
  onRetry: () => void
  onResetStatements: () => void
  resetting: boolean
}

/**
 * What Postgres says about itself.
 *
 * The other tabs are the API's view and see only what these two processes issued. This one covers
 * every connection to the server, which is where a query from the analytics worker, the importer, or
 * somebody at a psql prompt shows up — and that is usually the query nobody could find.
 */
export function DatabaseTab({
  data,
  loading,
  error,
  onRetry,
  onResetStatements,
  resetting,
}: DatabaseTabProps) {
  const [confirmReset, setConfirmReset] = useState(false)
  const [statementPage, setStatementPage] = useState(0)
  const [statementSize, setStatementSize] = useState(10)
  const [tablePage, setTablePage] = useState(0)
  const [tableSize, setTableSize] = useState(10)

  const connectionUse = data && data.maxConnections > 0 ? (data.connections / data.maxConnections) * 100 : 0

  const statementColumns: DataTableColumn<PerfPgStatement>[] = [
    {
      key: 'query',
      header: 'Statement',
      primary: true,
      width: '50%',
      render: (row) => <SqlBlock sql={row.query} maxLines={3} />,
    },
    { key: 'calls', header: 'Calls', align: 'right', render: (row) => formatCount(row.calls) },
    {
      key: 'mean',
      header: 'Mean',
      align: 'right',
      render: (row) => (
        <Typography variant="body2" color={`${latencySeverity(row.meanMs)}.main`} fontWeight="bold">
          {formatMs(row.meanMs)}
        </Typography>
      ),
    },
    {
      key: 'max',
      header: 'Max',
      align: 'right',
      hideBelow: 'md',
      render: (row) => formatMs(row.maxMs),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (row) => (
        <Stack alignItems="flex-end">
          <Typography variant="body2" fontWeight="bold">
            {formatMs(row.totalMs)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatPercent(row.sharePercent)} of all
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'cache',
      header: 'From cache',
      align: 'right',
      hideBelow: 'lg',
      // A statement reading mostly from disk is either scanning something it should not be, or
      // touching data that no longer fits in memory. Either way it is the next thing to look at.
      render: (row) =>
        row.cacheHitPercent == null ? (
          '—'
        ) : (
          <Typography variant="body2" color={row.cacheHitPercent < 90 ? 'warning.main' : 'text.primary'}>
            {formatPercent(row.cacheHitPercent)}
          </Typography>
        ),
    },
  ]

  const tableColumns: DataTableColumn<PerfTableStat>[] = [
    {
      key: 'name',
      header: 'Table',
      primary: true,
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: 12,
            minWidth: 0,
            wordBreak: 'break-all',
          }}
        >
          {row.tableName}
        </Typography>
      ),
    },
    { key: 'total', header: 'Total', align: 'right', render: (row) => row.totalSize },
    { key: 'table', header: 'Data', align: 'right', hideBelow: 'md', render: (row) => row.tableSize },
    {
      key: 'index',
      header: 'Indexes',
      align: 'right',
      hideBelow: 'md',
      render: (row) => row.indexSize,
    },
    { key: 'rows', header: 'Rows', align: 'right', render: (row) => formatCount(row.estimatedRows) },
    {
      key: 'dead',
      header: 'Dead rows',
      align: 'right',
      hideBelow: 'lg',
      // Dead rows above roughly a fifth of live ones means autovacuum is behind, and a bloated
      // table is slow to scan no matter how good its indexes are.
      render: (row) => {
        const bloated = row.estimatedRows > 0 && row.deadRows > row.estimatedRows * 0.2
        return (
          <Typography variant="body2" color={bloated ? 'warning.main' : 'text.secondary'}>
            {formatCount(row.deadRows)}
          </Typography>
        )
      },
    },
  ]

  return (
    <Stack spacing={2}>
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Could not read the database's own statistics.
          {data ? ' Showing the last successful reading.' : ''}
        </Alert>
      )}

      {data && !data.statStatementsAvailable && (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {data.statStatementsMessage}
          </Typography>
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile
            label="Database size"
            value={data?.databaseSize ?? '—'}
            detail={data?.serverVersion ? `Postgres ${data.serverVersion.split(' ')[0]}` : undefined}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile
            label="Connections"
            value={`${data?.connections ?? 0} / ${data?.maxConnections ?? 0}`}
            detail={`${data?.activeConnections ?? 0} active, ${data?.idleInTransaction ?? 0} idle in transaction`}
            severity={connectionUse > 80 ? 'error' : connectionUse > 60 ? 'warning' : undefined}
            help="Idle-in-transaction connections hold locks and block vacuum. Any sustained count there is a bug in a caller."
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile
            label="Cache hit ratio"
            value={formatPercent(data?.cacheHitRatioPercent, 2)}
            detail="Block reads served from memory"
            severity={
              data == null
                ? undefined
                : data.cacheHitRatioPercent > 99
                  ? 'success'
                  : data.cacheHitRatioPercent > 95
                    ? 'warning'
                    : 'error'
            }
            help="Counted since the server started. Below about 99% usually means the working set no longer fits in shared buffers."
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatTile
            label="Deadlocks"
            value={formatCount(data?.deadlocks)}
            detail={`${formatCount(data?.tempFiles)} temp files`}
            severity={data && data.deadlocks > 0 ? 'error' : undefined}
            help="Both are cumulative since the server started. Temp files mean a query did not fit in work_mem and spilled to disk."
            loading={loading}
          />
        </Grid>
      </Grid>

      {data && data.maxConnections > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Connection pool use
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, connectionUse)}
            color={connectionUse > 80 ? 'error' : connectionUse > 60 ? 'warning' : 'primary'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {data?.statStatementsAvailable && (
        <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Heaviest statements
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Every connection to this server, not just the APIs. Counted since the server started or since
                the last reset.
              </Typography>
            </Box>

            <Button
              size="small"
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={() => setConfirmReset(true)}
              disabled={resetting}
            >
              Reset counters
            </Button>
          </Stack>

          <DataTable
            rows={data.topStatements}
            columns={statementColumns}
            getRowKey={(row) => row.query.slice(0, 120) + row.calls}
            page={statementPage}
            pageSize={statementSize}
            onPageChange={setStatementPage}
            onPageSizeChange={setStatementSize}
            pageSizeOptions={[10, 25, 50]}
            emptyMessage="No statements recorded."
          />
        </Paper>
      )}

      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Running now
        </Typography>
        {(data?.activeQueries.length ?? 0) === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing is running. Refreshes every fifteen seconds.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {data?.activeQueries.map((query: PerfActiveQuery) => (
              <Box key={query.pid}>
                <Stack
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ mb: 0.5, columnGap: 1, rowGap: 0.5 }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={`${latencySeverity(query.runningForMs)}.main`}
                  >
                    {formatMs(query.runningForMs)}
                  </Typography>
                  <Chip label={query.state ?? 'unknown'} size="small" variant="outlined" />
                  {query.applicationName && (
                    <Chip label={query.applicationName} size="small" variant="outlined" />
                  )}
                  {query.waitEvent && (
                    // A long-running query that is WAITING is blocked on something else, which is a
                    // completely different problem from one that is busy.
                    <Chip label={`waiting: ${query.waitEvent}`} size="small" color="warning" />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    pid {query.pid}
                    {query.clientAddress ? ` from ${query.clientAddress}` : ''}
                  </Typography>
                </Stack>
                {query.query && <SqlBlock sql={query.query} maxLines={3} />}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Largest tables
        </Typography>
        <DataTable
          rows={data?.largestTables ?? []}
          columns={tableColumns}
          getRowKey={(row) => row.tableName}
          loading={loading}
          page={tablePage}
          pageSize={tableSize}
          onPageChange={setTablePage}
          onPageSizeChange={setTableSize}
          pageSizeOptions={[10, 25, 50]}
          emptyMessage="No tables reported."
        />
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Sequential scan hotspots
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Large tables being read by scanning them. Small tables are excluded: scanning those is correct
              and listing them would train you to ignore this.
            </Typography>

            {(data?.sequentialScanHotspots.length ?? 0) === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing flagged.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {data?.sequentialScanHotspots.map((row: PerfSeqScanTable) => (
                  <Stack
                    key={row.tableName}
                    direction="row"
                    alignItems="baseline"
                    sx={{ flexWrap: 'wrap', columnGap: 1 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 12,
                        flex: '1 1 140px',
                        minWidth: 0,
                        wordBreak: 'break-all',
                      }}
                    >
                      {row.tableName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCount(row.sequentialScans)} scans, {formatCount(row.indexScans)} index
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCount(row.rowsPerSequentialScan)} rows/scan
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Barely-used indexes
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Over a megabyte, scanned fewer than fifty times. Each one costs write throughput on every
              insert. Primary keys and unique constraints are excluded.
            </Typography>

            {(data?.unusedIndexes.length ?? 0) === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing flagged.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {data?.unusedIndexes.map((row: PerfUnusedIndex) => (
                  <Stack
                    key={row.indexName}
                    direction="row"
                    alignItems="baseline"
                    sx={{ flexWrap: 'wrap', columnGap: 1 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: 12,
                        flex: '1 1 140px',
                        minWidth: 0,
                        wordBreak: 'break-all',
                      }}
                    >
                      {row.indexName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.scans} scans
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {row.size}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={confirmReset}
        title="Reset statement counters?"
        message={
          'This clears pg_stat_statements for every database on this server, not just this one. ' +
          'Do it before testing a change so the next window measures only what happened after it.'
        }
        confirmLabel="Reset"
        onConfirm={() => {
          setConfirmReset(false)
          onResetStatements()
        }}
        onClose={() => setConfirmReset(false)}
      />
    </Stack>
  )
}
